import { useMemo } from 'react';
import { Building2, RotateCw, Search } from 'lucide-react';

import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { Input } from '@/components/ui/Input';
import { QueryError } from '@/components/ui/QueryError';
import { Select } from '@/components/ui/Select';
import { SkeletonLines } from '@/components/ui/Skeleton';
import {
  SCHOOL_STATUS_LABEL,
  SCHOOL_STATUS_OPTIONS,
  SCHOOL_STATUS_TONE,
  type InstituteStatus,
} from '@/constants/institute';
import type { Option } from '@/constants/institute';
import { cn, getNameInitials } from '@/lib/utils';
import type { ApiRequestError } from '@/lib/apiClient';
import type { InstituteListItem } from '@/types/institute';

interface InstitutePickerProps {
  institutes: InstituteListItem[];
  selectedUuid: string | null;
  onSelect: (uuid: string) => void;

  search: string;
  onSearchChange: (value: string) => void;
  /** Empty means every status — the shape `InstituteListParams` already uses. */
  status: InstituteStatus | '';
  onStatusChange: (value: InstituteStatus | '') => void;
  /** Puts both filters back to showing everything. */
  onClearFilters: () => void;

  isLoading: boolean;
  error: ApiRequestError | null;
  onRetry: () => void;
}

/**
 * Who we are looking at: every customer, filtered by where they stand.
 *
 * A list rather than a dropdown because the state of the account is part of
 * choosing one — a suspended institute's requirements are read differently from
 * a live one's, and a name in a `<select>` cannot say which it is.
 *
 * The four states an account can be in are the schema's own
 * (`SCHOOL_STATUS_LABEL`), so this screen and the institutes table cannot come
 * to disagree about what "Suspended" looks like. "Unsuspended" is not a fifth
 * state: lifting a suspension returns an institute to Active, which is what the
 * Active filter then shows.
 */
export function InstitutePicker({
  institutes,
  selectedUuid,
  onSelect,
  search,
  onSearchChange,
  status,
  onStatusChange,
  onClearFilters,
  isLoading,
  error,
  onRetry,
}: InstitutePickerProps) {
  /**
   * "All statuses" is a real option rather than a placeholder.
   *
   * A placeholder only shows while nothing is chosen — once somebody picks
   * Suspended there is nothing in the list to pick to get back, and the filter
   * can only be undone by reloading the page. An empty-valued option at the top
   * is the way out, and it reads as the choice it is.
   */
  const statusOptions = useMemo<Option[]>(
    () => [{ value: '', label: 'All statuses' }, ...SCHOOL_STATUS_OPTIONS],
    [],
  );

  const hasFilters = Boolean(search || status);

  return (
    <aside
      className={cn(
        'card flex min-h-0 flex-col overflow-hidden',
        // Stays beside the board on a wide screen while the checklist scrolls,
        // and is a plain block on a phone, where the two panes stack.
        'lg:sticky lg:top-6 lg:max-h-[calc(100vh-7rem)]',
      )}
    >
      <div className="flex flex-col gap-3 border-b border-line bg-surface-2 p-4">
        <div className="flex items-center justify-between gap-2">
          <p className="lbl">Institutes</p>
          {!isLoading && !error && (
            <span className="pill bg-surface text-ink-3">{institutes.length}</span>
          )}
        </div>

        <Input
          label="Search institutes"
          hideLabel
          placeholder="Search by name or code"
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
          leftIcon={<Search className="h-4 w-4" />}
        />

        <Select
          label="Filter institutes by status"
          hideLabel
          options={statusOptions}
          value={status}
          onChange={(event) => onStatusChange(event.target.value as InstituteStatus | '')}
        />

        {/* Offered only when there is something to clear — a button that cannot
            change anything is worse than one that is not there. */}
        {hasFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onClearFilters}
            leftIcon={<RotateCw className="h-3.5 w-3.5" />}
            className="self-start"
          >
            Clear filters
          </Button>
        )}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto p-2">
        {isLoading && (
          <div className="p-2">
            <SkeletonLines count={8} />
          </div>
        )}

        {!isLoading && error && (
          <div className="p-2">
            <QueryError title="Could not load the institutes" error={error} onRetry={onRetry} />
          </div>
        )}

        {!isLoading && !error && institutes.length === 0 && (
          <EmptyState
            icon={<Building2 className="h-6 w-6" />}
            title="No institutes match"
            description={
              hasFilters
                ? 'Try a different search, or clear the filters to see every institute.'
                : 'Institutes appear here as they are added to the platform.'
            }
            action={
              hasFilters ? (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={onClearFilters}
                  leftIcon={<RotateCw className="h-4 w-4" />}
                >
                  Clear filters
                </Button>
              ) : undefined
            }
          />
        )}

        {!isLoading && !error && institutes.length > 0 && (
          <ul className="flex flex-col gap-1">
            {institutes.map((institute) => {
              const isSelected = institute.uuid === selectedUuid;

              return (
                <li key={institute.uuid}>
                  <button
                    type="button"
                    onClick={() => onSelect(institute.uuid)}
                    aria-current={isSelected ? 'true' : undefined}
                    className={cn(
                      'flex w-full items-center gap-3 rounded-field border-l-2 p-2.5 text-left',
                      'transition-colors duration-150',
                      'focus:outline-none focus:ring-2 focus:ring-accent',
                      isSelected
                        ? 'border-l-accent bg-accent-soft'
                        : 'border-l-transparent hover:bg-surface-2',
                    )}
                  >
                    {/* Initials rather than a logo: the list is read by name, and
                        a mark that has to be fetched per row would make it wait. */}
                    <span
                      aria-hidden
                      className={cn(
                        'grid h-9 w-9 shrink-0 place-items-center rounded-field text-2xs font-[650]',
                        isSelected
                          ? 'bg-accent text-surface'
                          : 'bg-surface-2 text-ink-2',
                      )}
                    >
                      {getNameInitials(institute.displayName)}
                    </span>

                    <span className="min-w-0 flex-1">
                      <span
                        className={cn(
                          'block truncate text-sm font-[560]',
                          isSelected ? 'text-accent' : 'text-ink',
                        )}
                      >
                        {institute.displayName}
                      </span>
                      <span className="block truncate text-2xs text-ink-3">
                        {institute.code} · {institute.city}
                      </span>
                    </span>

                    <Badge tone={SCHOOL_STATUS_TONE[institute.status]}>
                      {SCHOOL_STATUS_LABEL[institute.status]}
                    </Badge>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </aside>
  );
}
